import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Save, Loader2, Upload, Camera, Trash2, AlertTriangle, Database, CalendarRange } from "lucide-react";

// Module-level: keeps input focus while typing (defining this inside the
// component was re-creating the type on every keystroke → focus loss).
const F = ({ k, label, type = "text", value, onChange, ...rest }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-brand-text-muted">{label}</Label>
    <Input
      type={type} value={value ?? ""} onChange={(e) => onChange(k, e.target.value)}
      data-testid={`settings-${k}`} className="mt-1.5" {...rest}
    />
  </div>
);

const CompanySettings = () => {
  const { brand, refresh } = useBrand();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [wipePreview, setWipePreview] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purging, setPurging] = useState(false);
  const [resharding, setResharding] = useState(false);

  const doReshard = async () => {
    if (!window.confirm("Move every driver_work_row into its correct pay period based on work_date? Creates missing historical periods. Safe to run multiple times.")) return;
    setResharding(true);
    try {
      const { data } = await api.post("/admin/maintenance/reshard-work-rows");
      toast.success(`Re-sharded ${data.moved} row(s). ${data.unchanged} already correct. ${data.periods_touched} period(s) touched.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-shard failed");
    }
    setResharding(false);
  };

  const loadWipePreview = async () => {
    try {
      const { data } = await api.get("/admin/maintenance/preview");
      setWipePreview(data);
    } catch (e) {
      // silent — button will still open
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/company/settings");
      setForm(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadWipePreview(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 800 * 1024) {
      toast.error(`Image is ${Math.round(file.size / 1024)} KB — please keep it under 800 KB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/company/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      await load();
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the uploaded logo? The default icon will be used again.")) return;
    setUploading(true);
    try {
      await api.delete("/company/settings/logo");
      await refresh();
      await load();
      toast.success("Logo removed");
    } catch (e) {
      toast.error("Remove failed");
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      // numeric coercions
      if (payload.vat_percentage !== undefined) payload.vat_percentage = parseFloat(payload.vat_percentage);
      if (payload.flat_rate_vat_percentage !== undefined) payload.flat_rate_vat_percentage = parseFloat(payload.flat_rate_vat_percentage);
      await api.patch("/company/settings", payload);
      await refresh();
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const doWipe = async () => {
    if (wipeConfirmText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post("/admin/maintenance/wipe-operational-data", { confirm: "DELETE" });
      const total = Object.values(data.deleted || {}).reduce((s, n) => s + n, 0);
      toast.success(`Wiped ${total} record(s). Drivers, users & company preserved.`);
      setWipeOpen(false);
      setWipeConfirmText("");
      await loadWipePreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    }
    setWiping(false);
  };

  const doPurgeDemo = async () => {
    if (purgeConfirmText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setPurging(true);
    try {
      const { data } = await api.post("/admin/maintenance/purge-demo-entities", { confirm: "DELETE" });
      const total = Object.values(data.deleted || {}).reduce((s, n) => s + n, 0);
      toast.success(`Removed ${total} demo record(s). They will not be re-seeded on redeploy.`);
      setPurgeOpen(false);
      setPurgeConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
    setPurging(false);
  };

  if (loading) return <div className="p-6 text-sm text-brand-text-muted">Loading…</div>;

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-4xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Branding · Company</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Company Settings</h1>
        <p className="text-sm text-brand-text-muted mt-1">Master data used across invoices, dashboards, and PDFs.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Brand block */}
        <Card className="p-6 rounded-2xl border-brand-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted mb-4">Brand identity</div>
          <div className="flex flex-col md:flex-row md:items-center gap-5 mb-6">
            <Logo variant="mark" size="lg" />
            <div className="flex-1">
              <div className="font-heading font-bold text-brand-text">Company logo</div>
              <div className="text-xs text-brand-text-muted mt-0.5">
                {brand.logo_url ? "Custom logo uploaded" : "Using the default MY CHOICE icon — upload yours below"}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <input
                  ref={galleryRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden" data-testid="logo-file-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
                <input
                  ref={cameraRef} type="file" accept="image/*" capture="environment"
                  className="hidden" data-testid="logo-camera-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
                <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  Choose file
                </Button>
                <Button type="button" variant="outline" className="md:hidden" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="logo-camera-btn">
                  <Camera className="w-4 h-4 mr-1.5" /> Camera
                </Button>
                {brand.logo_url && (
                  <Button type="button" variant="ghost" onClick={removeLogo} disabled={uploading} data-testid="logo-remove-btn">
                    <Trash2 className="w-4 h-4 mr-1.5 text-brand-text-muted" /> Remove
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-brand-text-muted mt-2">
                PNG / JPG / WEBP / SVG · max 800 KB · shown everywhere (sidebar, login, PDFs, favicon-style).
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F k="name" label="Company name" value={form.name} onChange={set} />
            <F k="logo_url" label="…or paste a public image URL" placeholder="https://…" value={form.logo_url} onChange={set} />
          </div>
        </Card>

        {/* Legal */}
        <Card className="p-6 rounded-2xl border-brand-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted mb-4">Legal & tax</div>
          <div className="grid md:grid-cols-2 gap-4">
            <F k="company_number" label="Company number" value={form.company_number} onChange={set} />
            <F k="vat_number" label="VAT number" value={form.vat_number} onChange={set} />
            <F k="vat_percentage" label="Standard VAT %" type="number" value={form.vat_percentage} onChange={set} />
            <F k="flat_rate_vat_percentage" label="Flat rate VAT %" type="number" value={form.flat_rate_vat_percentage} onChange={set} />
            <div className="md:col-span-2"><F k="registered_address" label="Registered address" value={form.registered_address} onChange={set} /></div>
          </div>
        </Card>

        {/* Banking */}
        <Card className="p-6 rounded-2xl border-brand-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted mb-4">Banking (for invoices)</div>
          <div className="grid md:grid-cols-2 gap-4">
            <F k="bank_name" label="Bank name" value={form.bank_name} onChange={set} />
            <F k="account_name" label="Account name" value={form.account_name} onChange={set} />
            <F k="sort_code" label="Sort code" value={form.sort_code} onChange={set} />
            <F k="account_number" label="Account number" value={form.account_number} onChange={set} />
            <F k="invoice_prefix" label="Invoice prefix" value={form.invoice_prefix} onChange={set} />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={busy} className="bg-brand-red hover:bg-brand-red-hover" data-testid="settings-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save changes
          </Button>
        </div>
      </form>

      {/* DANGER ZONE — wipe operational data */}
      <Card className="p-6 rounded-2xl border-red-200 bg-red-50/30" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-mono">Danger zone</div>
            <div className="font-heading font-bold text-brand-text mt-1">Wipe demo / operational data</div>
            <p className="text-sm text-brand-text-muted mt-1">
              Permanently delete <b>work rows, invoices, deductions, holidays, documents, DPD imports</b>
              {" "}and <b>pay periods</b> so you can start fresh with real DPD files.
              <br />
              <b>Preserved:</b> users, company settings, drivers, vehicles.
            </p>
            {wipePreview && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {Object.entries(wipePreview.will_delete).map(([k, v]) => (
                  <div key={k} className="p-2 rounded-lg bg-white border border-red-100">
                    <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">{k.replace(/_/g, " ")}</div>
                    <div className="font-heading font-bold text-red-700 mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button" variant="outline"
                onClick={() => { loadWipePreview(); setWipeOpen(true); }}
                className="border-red-300 text-red-700 hover:bg-red-100"
                data-testid="wipe-open-btn"
              >
                <Database className="w-4 h-4 mr-1.5" /> Wipe operational data…
              </Button>
              <Button
                type="button" variant="outline"
                onClick={() => setPurgeOpen(true)}
                className="border-red-400 text-red-800 hover:bg-red-100"
                data-testid="purge-demo-open-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Remove demo drivers/vehicles/users…
              </Button>
              <Button
                type="button" variant="outline"
                onClick={doReshard}
                disabled={resharding}
                className="border-brand-navy text-brand-navy hover:bg-brand-navy/10"
                data-testid="reshard-btn"
              >
                {resharding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarRange className="w-4 h-4 mr-1.5" />}
                Re-shard work rows into pay periods
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <AlertDialog open={wipeOpen} onOpenChange={(o) => { if (!o) setWipeConfirmText(""); setWipeOpen(o); }}>
        <AlertDialogContent data-testid="wipe-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Wipe operational data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <b>
                {wipePreview
                  ? Object.values(wipePreview.will_delete).reduce((s, n) => s + n, 0)
                  : "all"}{" "}
                record(s)
              </b>{" "}
              across work rows, invoices, deductions, holidays, documents, DPD imports, and pay periods.
              Users, company settings, drivers and vehicles are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs text-brand-text-muted">Type <b>DELETE</b> to confirm</Label>
            <Input
              value={wipeConfirmText}
              onChange={(e) => setWipeConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1.5 font-mono"
              data-testid="wipe-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wipe-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doWipe(); }}
              disabled={wipeConfirmText !== "DELETE" || wiping}
              className="bg-red-600 hover:bg-red-700"
              data-testid="wipe-confirm-btn"
            >
              {wiping ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Database className="w-4 h-4 mr-1.5" />}
              Wipe now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purgeOpen} onOpenChange={(o) => { if (!o) setPurgeConfirmText(""); setPurgeOpen(o); }}>
        <AlertDialogContent data-testid="purge-demo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" /> Remove demo drivers, vehicles & staff?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deletes the 3 seeded demo drivers (Bayram, Petya, Malin),
              their vehicles, and the demo admin/manager/driver login accounts.
              The <b>Platform Owner login is preserved</b>. Demo data will not
              be re-created on future redeploys.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs text-brand-text-muted">Type <b>DELETE</b> to confirm</Label>
            <Input
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1.5 font-mono"
              data-testid="purge-demo-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-demo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doPurgeDemo(); }}
              disabled={purgeConfirmText !== "DELETE" || purging}
              className="bg-red-700 hover:bg-red-800"
              data-testid="purge-demo-confirm-btn"
            >
              {purging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Remove now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanySettings;
